import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { AppRoutes } from '@/constants/appRoutes'
import { useAppSelector } from '@/hooks/redux'
import type { RootState } from '@/store/store'

export interface StepGuardEvalContext<TSlice> {
  slice: TSlice
  searchParams: URLSearchParams
}

export interface StepGuardConfig<TStep extends string, TSlice> {
  selectSlice: (state: RootState) => TSlice
  prerequisites: Record<
    TStep,
    (evalContext: StepGuardEvalContext<TSlice>) => boolean
  >
  fallbacks: Record<TStep, AppRoutes>
  onFail?: (step: TStep) => void
}

export const createStepGuard = <TStep extends string, TSlice>(
  config: StepGuardConfig<TStep, TSlice>,
) => {
  return (step: TStep) => {
    const slice = useAppSelector(config.selectSlice)
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
      const evalContext: StepGuardEvalContext<TSlice> = {
        slice,
        searchParams: new URLSearchParams(location.search),
      }
      if (!config.prerequisites[step](evalContext)) {
        config.onFail?.(step)
        navigate(config.fallbacks[step], { replace: true })
      }
    }, [step, slice, navigate, location.search])
  }
}
